import React, { useCallback, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import Cropper, { Area } from 'react-easy-crop';

interface AvatarCropDialogProps {
  open: boolean;
  /** Object URL (or data URL) of the source image to crop. */
  imageSrc: string | null;
  /** Output size in px of the square crop (default 512). */
  outputSize?: number;
  onCancel: () => void;
  /** Returns the cropped image as a JPEG File (1:1). */
  onConfirm: (file: File) => void;
}

/** Load an image element (needed to draw the crop to a canvas). */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', (e) => reject(e));
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = src;
  });
}

/** Draw the selected crop area to a square canvas and return a JPEG File. */
async function getCroppedFile(
  imageSrc: string,
  crop: Area,
  outputSize: number,
): Promise<File> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas não suportado.');

  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    image,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    outputSize,
    outputSize,
  );

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.9),
  );
  if (!blob) throw new Error('Falha ao gerar a imagem.');
  return new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
}

const AvatarCropDialog: React.FC<AvatarCropDialogProps> = ({
  open,
  imageSrc,
  outputSize = 512,
  onCancel,
  onConfirm,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_area: Area, areaPx: Area) => {
    setAreaPixels(areaPx);
  }, []);

  const reset = useCallback(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setAreaPixels(null);
  }, []);

  const handleConfirm = async (): Promise<void> => {
    if (!imageSrc || !areaPixels) return;
    setSaving(true);
    try {
      const file = await getCroppedFile(imageSrc, areaPixels, outputSize);
      onConfirm(file);
      reset();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onCancel}
      fullWidth
      maxWidth="xs"
      slotProps={{ transition: { onExited: reset } }}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>Ajustar foto</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Arraste para posicionar e use o zoom. A área circular é o que aparecerá no seu avatar.
        </Typography>
        {/* Square crop stage with a round overlay */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '1 / 1',
            bgcolor: '#111',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {imageSrc ? (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              restrictPosition
            />
          ) : null}
        </Box>

        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mt: 2 }}>
          <ZoomOutIcon sx={{ color: 'text.secondary' }} />
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.01}
            onChange={(_e, v) => setZoom(v as number)}
            aria-label="Zoom"
          />
          <ZoomInIcon sx={{ color: 'text.secondary' }} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleConfirm()}
          disabled={saving || !areaPixels}
        >
          {saving ? 'Processando…' : 'Aplicar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AvatarCropDialog;
